import React, { useState, useEffect, useMemo } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap'
import { useSnackbar } from 'notistack'
import PMBSelect from 'components/PMBSelect'
import useAutoFetchPublicVersion from 'hooks/useAutoFetchPublicVersion'
import useGetCommodityQuery from 'hooks/useGetCommodityQuery'
import {
  useCreatePriceTableMutation,
  useUpdatePriceTableMutation,
} from './priceTableApiSlice'

const ModalAddEditPrice = ({ isOpen, setIsOpen, data = null, refetch }) => {
  const { enqueueSnackbar } = useSnackbar()
  const isEditMode = Boolean(data)
  const autoFetchPublicVersion = useAutoFetchPublicVersion()

  const [createPriceTable, { isLoading: isCreating }] =
    useCreatePriceTableMutation()
  const [updatePriceTable, { isLoading: isUpdating }] =
    useUpdatePriceTableMutation()

  const [payload, setPayload] = useState({
    itemId: null,
    unit: null,
    price: '',
  })

  const { data: itemsGroupedData, isLoading: isLoadingItems } =
    useGetCommodityQuery()

  const itemOptions = useMemo(() => {
    if (!itemsGroupedData) return []
    return itemsGroupedData.map(group => ({
      ...group,
      options: group.options.map(opt => ({
        ...opt,
        value: opt.id
      }))
    }))
  }, [itemsGroupedData])

  const flatOptions = useMemo(() => itemOptions.flatMap(g => g.options || [g]), [itemOptions])

  useEffect(() => {
    if (isEditMode && data) {
      setPayload({
        itemId: data?.item
          ? {
              label: data.item.nameItem,
              value: data.item.id,
            }
          : null,
        unit: data?.unit ? { label: data.unit, value: data.unit } : null,
        price: data?.price || '',
      })
    } else {
      setPayload({
        itemId: null,
        unit: null,
        price: '',
      })
    }
  }, [isEditMode, data])


  const isSubmitDisabled = !payload.itemId || !payload.unit || !payload.price

  const handleSubmit = async () => {
    try {
      const submitData = {
        itemId: payload.itemId?.value,
        unit: payload.unit?.value || payload.unit?.label || payload.unit,
        price: Number(payload.price),
      }

      if (isEditMode) {
        await updatePriceTable({ id: data.id, ...submitData }).unwrap()
        enqueueSnackbar('Harga berhasil diubah', { variant: 'success' })
      } else {
        await createPriceTable(submitData).unwrap()
        enqueueSnackbar('Harga berhasil ditambahkan', { variant: 'success' })
      }
      setIsOpen(false)
      if (refetch) refetch()
    } catch (err) {
      enqueueSnackbar(
        err?.data?.message ||
          (isEditMode ? 'Gagal mengubah harga' : 'Gagal menambahkan harga'),
        { variant: 'error' }
      )
    }
  }

  return (
    <Modal data-testid={`price-table-modal-${isEditMode ? 'edit' : 'add'}`} isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} centered>
      <ModalHeader toggle={() => setIsOpen(!isOpen)}>
        {isEditMode ? 'Ubah Harga' : 'Tambah Harga Baru'}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="itemId">
            Barang <span className="text-danger">*</span>
          </Label>
          <PMBSelect
            testId="price-table-modal-commodity-select"
            inputId="itemId"
            placeholder="Pilih barang"
            options={itemOptions}
            value={payload.itemId}
            isLoading={isLoadingItems}
            onChange={(opt) =>
              setPayload((prev) => ({
                ...prev,
                itemId: opt,
              }))
            }
          />
        </FormGroup>

        <FormGroup>
          <Label for="unit">
            Satuan <span className="text-danger">*</span>
          </Label>
          <PMBSelect
            testId="price-table-modal-unit-select"
            inputId="unit"
            placeholder="Pilih satuan"
            options={autoFetchPublicVersion?.unitArray ?? []}
            value={payload.unit}
            onChange={(opt) =>
              setPayload((prev) => ({
                ...prev,
                unit: opt,
              }))
            }
          />
        </FormGroup>

        <FormGroup>
          <Label for="price">
            Harga <span className="text-danger">*</span>
          </Label>
          <Input
            data-testid="price-table-modal-price-input"
            id="price"
            type="number"
            placeholder="Masukkan harga"
            value={payload.price}
            onChange={(e) =>
              setPayload((prev) => ({ ...prev, price: e.target.value }))
            }
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" data-testid="price-table-modal-cancel-button" onClick={() => setIsOpen(false)}>
          Batal
        </Button>
        <Button
          color="primary"
          data-testid="price-table-modal-save-button"
          disabled={isSubmitDisabled || isCreating || isUpdating}
          onClick={handleSubmit}
        >
          {isCreating || isUpdating ? (
            <Spinner size="sm" />
          ) : (
            'Simpan'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ModalAddEditPrice
